/**
 * Toast 总线：把"发提示"这件事从组件树里解耦出来。
 *
 * 为什么需要：错误提示的触发点遍布各处，其中不少在 React 组件之外
 * （`api/web.ts` 的请求层、`utils/exportFormats.ts` 的导出流程、
 * `utils/draftStore.ts` 的 IndexedDB 降级）。这些地方拿不到 `useToast()`。
 *
 * 用法：`ToastProvider` 挂载时注册真正的实现；任何模块都能 `notifyError(...)`。
 * 未注册（如单元测试环境）时静默降级到 console，不会抛错。
 */
export interface ToastApi {
  success: (message: string, action?: { label: string; onClick: () => void }) => void;
  error: (message: string, action?: { label: string; onClick: () => void }) => void;
  warn: (message: string, action?: { label: string; onClick: () => void }) => void;
  info: (message: string, action?: { label: string; onClick: () => void }) => void;
}

let current: ToastApi | null = null;

/** 由 ToastProvider 调用；返回取消注册函数 */
export function registerToastApi(api: ToastApi): () => void {
  current = api;
  return () => {
    if (current === api) current = null;
  };
}

/** 当前是否有真实的 toast 实现（用于测试断言） */
export function hasToastApi(): boolean {
  return current !== null;
}

function fallback(kind: string, message: string): void {
  // 无 UI 上下文（单测/SSR）时退化为 console，避免抛错影响主流程
  // eslint-disable-next-line no-console
  console.warn(`[notify:${kind}] ${message}`);
}

export function notifySuccess(message: string, action?: { label: string; onClick: () => void }): void {
  if (current) current.success(message, action); else fallback('success', message);
}

export function notifyError(message: string, action?: { label: string; onClick: () => void }): void {
  if (current) current.error(message, action); else fallback('error', message);
}

export function notifyWarn(message: string, action?: { label: string; onClick: () => void }): void {
  if (current) current.warn(message, action); else fallback('warn', message);
}

export function notifyInfo(message: string, action?: { label: string; onClick: () => void }): void {
  if (current) current.info(message, action); else fallback('info', message);
}

/**
 * 从未知的 catch 值里提取可读信息。
 * 统一走这里，避免各处写出 `String(e)` / `e.message` 混用导致 `[object Object]`。
 */
export function errorMessage(e: unknown, fallbackText = '操作失败，请重试'): string {
  if (typeof e === 'string' && e.trim()) return e;
  if (e instanceof Error && e.message) return e.message;
  if (e && typeof e === 'object') {
    const maybe = (e as { error?: unknown; message?: unknown });
    if (typeof maybe.error === 'string' && maybe.error) return maybe.error;
    if (typeof maybe.message === 'string' && maybe.message) return maybe.message;
  }
  return fallbackText;
}

/**
 * 上报一次失败：写入统一 logger 并发 toast。
 * 用于替换散落的 `catch {}` —— 失败必须可见、可诊断。
 */
export function reportError(scope: string, e: unknown, fallbackText?: string): string {
  const message = errorMessage(e, fallbackText);
  // 动态导入避免 utils 层在无 UI 上下文时产生副作用依赖
  void import('./logger').then(({ logger }) => {
    logger.error(`${scope}: ${message}`, { error: e });
  }).catch(() => { /* 日志失败不影响提示 */ });
  notifyError(`${scope}：${message}`);
  return message;
}
